import { useRef, useState, type DragEvent } from 'react';

export interface UploadDropzoneProps {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  multiple?: boolean;
}

export function UploadDropzone({ onFiles, disabled = false, multiple = false }: UploadDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const acceptFiles = (files: FileList | null) => {
    if (!files || disabled) return;
    onFiles(Array.from(files));
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    acceptFiles(event.dataTransfer.files);
  };

  return (
    <div
      className={`rounded-card border border-dashed p-6 text-center transition-colors duration-200 motion-reduce:transition-none ${
        dragging ? 'border-accent bg-accent-tint' : 'border-line bg-elevated'
      }`}
      onDragEnter={(event) => {
        event.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
      }}
      onDrop={handleDrop}
    >
      <input
        ref={inputRef}
        className="sr-only"
        type="file"
        accept="video/mp4,.mp4"
        multiple={multiple}
        disabled={disabled}
        onChange={(event) => {
          acceptFiles(event.currentTarget.files);
          event.currentTarget.value = '';
        }}
      />
      <span className="font-mono text-xs font-semibold uppercase tracking-[0.14em] text-faint">
        Review proxy
      </span>
      <p className="mt-2 text-muted">
        {multiple ? 'Drop your MP4 files here.' : 'Drop an MP4 file here.'}
      </p>
      <button
        className="mt-4 inline-flex min-h-11 min-w-11 items-center justify-center rounded-full border border-accent px-5 font-semibold text-accent transition-colors duration-200 hover:bg-accent-tint disabled:cursor-not-allowed disabled:opacity-60"
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        Choose {multiple ? 'files' : 'a file'}
      </button>
      <p className="mt-3 text-sm text-faint">
        MP4 only. Uploads start automatically and go straight to private storage.
      </p>
    </div>
  );
}
